import {
  ButtonMenu,
  column,
  GridColumn,
  GridDataRow,
  GridTable,
  ScrollableContent,
  selectColumn,
  simpleDataRows,
  SimpleHeaderAndData,
  useComputed,
  useGridTableApi,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { SearchBox, tagCell } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import {
  useActivateUsersMutation,
  useDeactivateUsersMutation,
  UsersPageQuery,
  useUsersPageQuery,
} from "src/routes/admin/users/UsersPage.generated";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { queryResult } from "src/utils";

/** Lists all internal users and allows deactivating/activating. */
export function UsersPage() {
  const [searchFilter, setSearchFilter] = useState<string>("");
  const query = useUsersPageQuery();
  const [activateUsers] = useActivateUsersMutation({ refetchQueries: ["UsersPage"] });
  const [deactivateUsers] = useDeactivateUsersMutation({ refetchQueries: ["UsersPage"] });
  const tableApi = useGridTableApi<Row>();
  const isReadonly = !useFeatureFlag(FeatureFlagType.BlueprintAdmin);
  const columns = useMemo(() => (isReadonly ? readonlyColumns : editableColumns), [isReadonly]);

  const selectedRows = useComputed(() => tableApi.getSelectedRows("data"), [tableApi]);

  return queryResult(query, {
    data: ({ internalUsers }) => {
      return (
        <>
          <PageHeader title="Users" />
          <TableActions onlyRight>
            <SearchBox onSearch={setSearchFilter} />
            {!isReadonly && (
              <ButtonMenu
                items={[
                  {
                    label: `Activate Selected Users (${selectedRows.length})`,
                    disabled: selectedRows.length === 0,
                    onClick: async () => {
                      await activateUsers({ variables: { input: { id: selectedRows.map((r) => r.data.user.id!) } } });
                      tableApi.clearSelections();
                    },
                  },
                  {
                    label: `Deactivate Selected Users (${selectedRows.length})`,
                    disabled: selectedRows.length === 0,
                    onClick: async () => {
                      await deactivateUsers({ variables: { input: { id: selectedRows.map((r) => r.data.user.id!) } } });
                      tableApi.clearSelections();
                    },
                  },
                ]}
                trigger={{ label: "Actions" }}
              />
            )}
          </TableActions>
          <ScrollableContent>
            <GridTable
              key={isReadonly ? "readonly" : "editable"}
              api={isReadonly ? undefined : tableApi}
              style={{ rowHeight: "fixed" }}
              columns={columns}
              rows={createRows(internalUsers)}
              filter={searchFilter}
              stickyHeader
            />
          </ScrollableContent>
        </>
      );
    },
  });
}

type Row = SimpleHeaderAndData<UsersPageQuery["internalUsers"][0]>;

const nameColumn = column<Row>({ id: "name", header: "Name", data: (d) => d.name });
const emailColumn = column<Row>({ id: "email", header: "Email", data: (d) => d.email });
const statusColumn = column<Row>({
  id: "status",
  header: "Status",
  data: (d) => tagCell(d.user?.isActive ? "success" : "neutral", d.user?.isActive ? "Active" : "Inactive"),
  w: "108px",
});

const editableColumns: GridColumn<Row>[] = [selectColumn<Row>({ w: "32px" }), nameColumn, emailColumn, statusColumn];

const readonlyColumns: GridColumn<Row>[] = [nameColumn, emailColumn, statusColumn];

function createRows(internalUsers: UsersPageQuery["internalUsers"]): GridDataRow<Row>[] {
  return simpleDataRows(internalUsers);
}
